import React, { useEffect, useState } from 'react';
import { API } from '../../api/client';
import { QUESTION_FORMATS, formatForPattern } from '../questionFormats';

// 'link' renders as a plain text input for the applicant, but as a clickable
// anchor in the evaluator scoring view.
const QUESTION_TYPES = ['text', 'textarea', 'number', 'currency', 'select', 'checkboxes', 'file', 'video', 'link'];

// Scoring-view rendering hints (see backend display_hint). '' = plain label/value.
const DISPLAY_HINTS: { value: string; label: string }[] = [
  { value: '', label: 'Text (default)' },
  { value: 'chips', label: 'Chips' },
  { value: 'cert', label: 'Certification ✓/✗' },
  { value: 'esg', label: 'ESG toggles' },
  { value: 'file', label: 'File / upload' },
];

interface Question {
  id: number;
  label: string;
  type: string;
  required?: boolean;
  options?: string[];
  note?: string;
  hint?: string;
  pattern?: string;
  patternHint?: string;
  displayHint?: string;
  scoringHidden?: boolean;
  scoringLabel?: string;
  subgroup?: string;
  fieldKey?: string;
  searchable?: boolean;
  groupThousands?: boolean;
}

interface FieldKeyOption {
  key: string;
  label: string;
}

interface Section {
  id: string;
  title: string;
  shortLabel?: string;
  questions: Question[];
  questionCount: number;
}

interface SectionDraft {
  id?: string;        // undefined for a newly-added section (server assigns a slug)
  title: string;
  shortLabel: string;
  questionCount: number;
}

interface EditState {
  label: string;
  type: string;
  required: boolean;
  options: string; // comma-separated
  note: string;
  hint: string;
  pattern: string;
  patternHint: string;
  displayHint: string;
  scoringHidden: boolean;
  scoringLabel: string;
  subgroup: string;
  fieldKey: string;
  searchable: boolean;
  groupThousands: boolean;
}

export default function AdminQuestions() {
  const [sections, setSections] = useState<Section[]>([]);
  const [fieldKeyOptions, setFieldKeyOptions] = useState<FieldKeyOption[]>([]);
  const [activeSection, setActiveSection] = useState('');
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editState, setEditState] = useState<EditState | null>(null);
  const [saving, setSaving] = useState(false);
  const [savedMsg, setSavedMsg] = useState('');
  const [error, setError] = useState('');
  const [addingNew, setAddingNew] = useState(false);
  const [newQ, setNewQ] = useState<EditState>({ label: '', type: 'text', required: false, options: '', note: '', hint: '', pattern: '', patternHint: '', displayHint: '', scoringHidden: false, scoringLabel: '', subgroup: '', fieldKey: '', searchable: false, groupThousands: false });

  // Cohort year state.
  const [year, setYear] = useState<number | null>(null);
  const [activeYear, setActiveYear] = useState<number | null>(null);
  const [availableYears, setAvailableYears] = useState<number[]>([]);
  const [creatingYear, setCreatingYear] = useState(false);
  const [newYearValue, setNewYearValue] = useState<number>(0);
  const [yearMsg, setYearMsg] = useState('');
  const [yearErr, setYearErr] = useState('');

  // Section-management state (add / rename / reorder / delete sections).
  const [editingSections, setEditingSections] = useState(false);
  const [sectionDrafts, setSectionDrafts] = useState<SectionDraft[]>([]);
  const [savingSections, setSavingSections] = useState(false);
  const [sectionMsg, setSectionMsg] = useState('');
  const [sectionErr, setSectionErr] = useState('');

  const loadQuestions = (y?: number) => {
    setLoading(true);
    setEditingId(null);
    setEditState(null);
    setAddingNew(false);
    API.adminQuestions(y).then(data => {
      if (data?.sections) {
        setSections(data.sections);
        setFieldKeyOptions(data.fieldKeyOptions ?? []);
        setActiveSection(prev => (prev && data.sections.some((s: Section) => s.id === prev)) ? prev : (data.sections[0]?.id ?? ''));
        setYear(data.year ?? null);
        setActiveYear(data.activeYear ?? null);
        setAvailableYears(data.availableYears ?? []);
      }
      setLoading(false);
    });
  };

  useEffect(() => { loadQuestions(); }, []);

  const flashYear = (msg: string, isErr = false) => {
    if (isErr) { setYearErr(msg); setTimeout(() => setYearErr(''), 4000); }
    else { setYearMsg(msg); setTimeout(() => setYearMsg(''), 3000); }
  };

  const onSelectYear = (y: number) => { if (y !== year) loadQuestions(y); };

  const makeActive = async () => {
    if (year == null) return;
    try {
      await API.setActiveQuestionYear(year);
      setActiveYear(year);
      flashYear(`FY${String(year).slice(2)} is now the active application year — applicants see it immediately.`);
    } catch {
      flashYear('Could not set the active year. Please try again.', true);
    }
  };

  const startCreateYear = () => {
    const base = availableYears.length ? Math.max(...availableYears) : (activeYear ?? new Date().getFullYear());
    setNewYearValue(base + 1);
    setYearErr('');
    setCreatingYear(true);
  };

  const createYear = async () => {
    try {
      await API.seedQuestions(newYearValue);
      setCreatingYear(false);
      flashYear(`Created the ${newYearValue} question set from the previous year. Edit it below, then set it active when ready.`);
      loadQuestions(newYearValue);
    } catch {
      flashYear(`Could not create ${newYearValue} — questions for that year may already exist.`, true);
    }
  };

  // --- Section management -----------------------------------------------------
  const startEditSections = () => {
    setSectionDrafts(sections.map(s => ({
      id: s.id,
      title: s.title,
      shortLabel: s.shortLabel ?? '',
      questionCount: s.questions.length,
    })));
    setSectionErr('');
    setEditingSections(true);
  };

  const cancelEditSections = () => { setEditingSections(false); setSectionErr(''); };

  const setDraft = (i: number, field: 'title' | 'shortLabel', val: string) =>
    setSectionDrafts(prev => prev.map((d, j) => (j === i ? { ...d, [field]: val } : d)));

  const moveDraft = (i: number, dir: -1 | 1) =>
    setSectionDrafts(prev => {
      const a = [...prev];
      const j = i + dir;
      if (j < 0 || j >= a.length) return prev;
      [a[i], a[j]] = [a[j], a[i]];
      return a;
    });

  const addDraft = () =>
    setSectionDrafts(prev => [...prev, { title: '', shortLabel: '', questionCount: 0 }]);

  const removeDraft = (i: number) => {
    const d = sectionDrafts[i];
    // Guardrail: an existing section with questions can't be removed here — its
    // questions (and any submitted answers) would be orphaned. Move/delete them first.
    if (d.id && d.questionCount > 0) {
      setSectionErr(`"${d.title || 'This section'}" has ${d.questionCount} question${d.questionCount === 1 ? '' : 's'}. Move or delete them first.`);
      return;
    }
    setSectionErr('');
    setSectionDrafts(prev => prev.filter((_, j) => j !== i));
  };

  const saveSections = async () => {
    if (sectionDrafts.length === 0) { setSectionErr('Keep at least one section.'); return; }
    if (sectionDrafts.some(d => !d.title.trim())) { setSectionErr('Every section needs a title.'); return; }
    setSavingSections(true);
    setSectionErr('');
    try {
      await API.updateSections({
        sections: sectionDrafts.map(d => ({
          id: d.id,
          title: d.title.trim(),
          shortLabel: d.shortLabel.trim() || undefined,
        })),
      }, year ?? undefined);
      setEditingSections(false);
      setSectionMsg('Sections saved.');
      setTimeout(() => setSectionMsg(''), 3000);
      loadQuestions(year ?? undefined);
    } catch {
      setSectionErr('Could not save. A section that still has questions cannot be removed.');
    } finally {
      setSavingSections(false);
    }
  };

  const currentSection = sections.find(s => s.id === activeSection);

  // Subgroup headings already in use in this section — offered as datalist
  // suggestions so admins reuse an existing heading rather than typo a new one.
  const subgroupOptions = Array.from(
    new Set((currentSection?.questions ?? []).map(q => q.subgroup?.trim()).filter(Boolean) as string[])
  );

  const startEdit = (q: Question) => {
    setEditingId(q.id);
    setEditState({
      label: q.label,
      type: q.type,
      required: q.required ?? false,
      options: (q.options ?? []).join(', '),
      note: q.note ?? '',
      hint: q.hint ?? '',
      pattern: q.pattern ?? '',
      patternHint: q.patternHint ?? '',
      displayHint: q.displayHint ?? '',
      scoringHidden: q.scoringHidden ?? false,
      scoringLabel: q.scoringLabel ?? '',
      subgroup: q.subgroup ?? '',
      fieldKey: q.fieldKey ?? '',
      searchable: q.searchable ?? false,
      groupThousands: q.groupThousands ?? false,
    });
    setAddingNew(false);
  };

  const cancelEdit = () => { setEditingId(null); setEditState(null); };

  const applyEdit = () => {
    if (!editState || editingId === null) return;
    setSections(prev => prev.map(s => {
      if (s.id !== activeSection) return s;
      return {
        ...s,
        questions: s.questions.map(q => q.id !== editingId ? q : {
          ...q,
          label: editState.label,
          type: editState.type,
          required: editState.required || undefined,
          options: needsOptions(editState.type) ? editState.options.split(',').map(o => o.trim()).filter(Boolean) : undefined,
          note: editState.note || undefined,
          hint: editState.hint || undefined,
          pattern: editState.type === 'text' && editState.pattern ? editState.pattern : undefined,
          patternHint: editState.type === 'text' && editState.pattern ? (editState.patternHint || undefined) : undefined,
          displayHint: editState.displayHint || undefined,
          scoringHidden: editState.scoringHidden || undefined,
          scoringLabel: editState.scoringLabel.trim() || undefined,
          subgroup: editState.subgroup.trim() || undefined,
          fieldKey: editState.fieldKey || undefined,
          searchable: (editState.type === 'select' && editState.searchable) || undefined,
          groupThousands: (editState.type === 'number' && editState.groupThousands) || undefined,
        }),
      };
    }));
    setEditingId(null);
    setEditState(null);
  };

  const deleteQuestion = (id: number) => {
    setSections(prev => prev.map(s => {
      if (s.id !== activeSection) return s;
      return { ...s, questions: s.questions.filter(q => q.id !== id) };
    }));
  };

  const moveQuestion = (id: number, dir: -1 | 1) => {
    setSections(prev => prev.map(s => {
      if (s.id !== activeSection) return s;
      const qs = [...s.questions];
      const i = qs.findIndex(q => q.id === id);
      const j = i + dir;
      if (j < 0 || j >= qs.length) return s;
      [qs[i], qs[j]] = [qs[j], qs[i]];
      return { ...s, questions: qs };
    }));
  };

  const commitNewQuestion = () => {
    if (!newQ.label.trim()) return;
    const section = sections.find(s => s.id === activeSection);
    if (!section) return;
    const maxId = section.questions.reduce((m, q) => Math.max(m, q.id), 0);
    const q: Question = {
      id: maxId + 1,
      label: newQ.label,
      type: newQ.type,
      required: newQ.required || undefined,
      options: needsOptions(newQ.type) ? newQ.options.split(',').map(o => o.trim()).filter(Boolean) : undefined,
      note: newQ.note || undefined,
      hint: newQ.hint || undefined,
      pattern: newQ.type === 'text' && newQ.pattern ? newQ.pattern : undefined,
      patternHint: newQ.type === 'text' && newQ.pattern ? (newQ.patternHint || undefined) : undefined,
      displayHint: newQ.displayHint || undefined,
      scoringHidden: newQ.scoringHidden || undefined,
      scoringLabel: newQ.scoringLabel.trim() || undefined,
      subgroup: newQ.subgroup.trim() || undefined,
      fieldKey: newQ.fieldKey || undefined,
      searchable: (newQ.type === 'select' && newQ.searchable) || undefined,
      groupThousands: (newQ.type === 'number' && newQ.groupThousands) || undefined,
    };
    setSections(prev => prev.map(s =>
      s.id !== activeSection ? s : { ...s, questions: [...s.questions, q] }
    ));
    setNewQ({ label: '', type: 'text', required: false, options: '', note: '', hint: '', pattern: '', patternHint: '', displayHint: '', scoringHidden: false, scoringLabel: '', subgroup: '', fieldKey: '', searchable: false, groupThousands: false });
    setAddingNew(false);
  };

  const saveSection = async () => {
    if (!currentSection) return;
    setSaving(true);
    setError('');
    try {
      await API.updateSectionQuestions(currentSection.id, { questions: currentSection.questions }, year ?? undefined);
      setSavedMsg(`"${currentSection.title}" saved.`);
      setTimeout(() => setSavedMsg(''), 3000);
    } catch {
      setError('Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="phil-loading">Loading…</div>;

  return (
    <>
      <div className="phil-page-heading">Application Questions</div>
      <div className="phil-page-sub">Edit question labels, types, and options. Edits to the active year apply to applicants immediately after saving.</div>

      {/* Cohort year bar */}
      <div className="aq-yearbar">
        <div className="aq-yearbar-group">
          <label className="aq-yearbar-label">Cohort year</label>
          <select
            className="phil-select"
            style={{ width: 'auto', minWidth: 90 }}
            value={year ?? ''}
            onChange={e => onSelectYear(Number(e.target.value))}
          >
            {year != null && !availableYears.includes(year) && <option value={year}>{year}</option>}
            {availableYears.map(y => (
              <option key={y} value={y}>{y}{y === activeYear ? ' • active' : ''}</option>
            ))}
          </select>
          {year === activeYear ? (
            <span className="aq-active-pill">● Applicants see this year</span>
          ) : (
            <button className="phil-btn phil-btn-primary phil-btn-sm" onClick={makeActive}>
              Set as active for applicants
            </button>
          )}
        </div>

        <div className="aq-yearbar-group">
          {yearMsg && <span style={{ fontSize: 12, color: '#28C840', fontWeight: 600 }}>{yearMsg}</span>}
          {yearErr && <span style={{ fontSize: 12, color: '#E24B4A' }}>{yearErr}</span>}
          {!creatingYear ? (
            <button className="phil-btn phil-btn-secondary phil-btn-sm" onClick={startCreateYear}>
              + New cohort year
            </button>
          ) : (
            <span className="aq-create-year">
              <input
                type="number"
                className="phil-input"
                style={{ width: 90 }}
                value={newYearValue}
                onChange={e => setNewYearValue(Number(e.target.value))}
              />
              <button className="phil-btn phil-btn-primary phil-btn-sm" onClick={createYear}>Create</button>
              <button className="phil-btn phil-btn-secondary phil-btn-sm" onClick={() => setCreatingYear(false)}>Cancel</button>
              <span className="aq-create-hint">Copies the previous year's questions as a starting point.</span>
            </span>
          )}
        </div>
      </div>

      {/* Section tabs */}
      <div style={{ display: 'flex', gap: 0, borderBottom: '2px solid #e2e2e2', marginBottom: 20, marginTop: 16, alignItems: 'flex-end' }}>
        {sections.map(s => (
          <div
            key={s.id}
            onClick={() => { setActiveSection(s.id); cancelEdit(); setAddingNew(false); }}
            style={{
              padding: '9px 18px',
              fontSize: 13,
              fontWeight: activeSection === s.id ? 700 : 500,
              color: activeSection === s.id ? '#00AEEF' : '#555',
              borderBottom: activeSection === s.id ? '2px solid #00AEEF' : '2px solid transparent',
              marginBottom: -2,
              cursor: 'pointer',
              whiteSpace: 'nowrap',
            }}
          >
            {s.title}
            <span style={{ fontSize: 11, color: '#aaa', marginLeft: 6, fontWeight: 400 }}>({s.questions.length})</span>
          </div>
        ))}
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center', paddingBottom: 6 }}>
          {sectionMsg && <span style={{ fontSize: 12, color: '#28C840', fontWeight: 600 }}>{sectionMsg}</span>}
          {!editingSections && (
            <button className="phil-btn phil-btn-secondary phil-btn-sm" onClick={startEditSections}>
              ✎ Edit sections
            </button>
          )}
        </div>
      </div>

      {/* Section editor: add / rename / reorder / delete sections */}
      {editingSections && (
        <div className="phil-card" style={{ marginBottom: 20 }}>
          <div className="phil-card-head">
            <h2>Edit Sections</h2>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              {sectionErr && <span style={{ fontSize: 12, color: '#E24B4A' }}>{sectionErr}</span>}
              <button className="phil-btn phil-btn-primary phil-btn-sm" onClick={saveSections} disabled={savingSections}>
                {savingSections ? 'Saving…' : 'Save Sections'}
              </button>
              <button className="phil-btn phil-btn-secondary phil-btn-sm" onClick={cancelEditSections}>Cancel</button>
            </div>
          </div>
          <table className="phil-table">
            <thead>
              <tr>
                <th style={{ width: 56 }}>#</th>
                <th>Title <span style={{ fontWeight: 400, color: '#aaa' }}>(heading applicants see)</span></th>
                <th>Short label <span style={{ fontWeight: 400, color: '#aaa' }}>(step nav chip)</span></th>
                <th style={{ width: 90 }}>Questions</th>
                <th style={{ width: 90 }}></th>
              </tr>
            </thead>
            <tbody>
              {sectionDrafts.map((d, i) => (
                <tr key={d.id ?? `new-${i}`}>
                  <td style={{ color: '#aaa', fontSize: 12 }}>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                      <button
                        className="phil-btn phil-btn-secondary phil-btn-sm"
                        style={{ padding: '0 5px', fontSize: 11, lineHeight: '16px', minWidth: 0, visibility: i === 0 ? 'hidden' : 'visible' }}
                        onClick={() => moveDraft(i, -1)}
                        title="Move up"
                      >▲</button>
                      <span>{i + 1}</span>
                      <button
                        className="phil-btn phil-btn-secondary phil-btn-sm"
                        style={{ padding: '0 5px', fontSize: 11, lineHeight: '16px', minWidth: 0, visibility: i === sectionDrafts.length - 1 ? 'hidden' : 'visible' }}
                        onClick={() => moveDraft(i, 1)}
                        title="Move down"
                      >▼</button>
                    </div>
                  </td>
                  <td>
                    <input
                      className="phil-input"
                      placeholder="Section title"
                      value={d.title}
                      onChange={e => setDraft(i, 'title', e.target.value)}
                    />
                  </td>
                  <td>
                    <input
                      className="phil-input"
                      placeholder="Defaults to title"
                      value={d.shortLabel}
                      onChange={e => setDraft(i, 'shortLabel', e.target.value)}
                    />
                  </td>
                  <td style={{ fontSize: 12, color: '#777' }}>
                    {d.id ? d.questionCount : <span style={{ color: '#00AEEF' }}>new</span>}
                  </td>
                  <td>
                    <button
                      className="phil-btn phil-btn-secondary phil-btn-sm"
                      style={{ color: d.id && d.questionCount > 0 ? '#ccc' : '#E24B4A' }}
                      onClick={() => removeDraft(i)}
                      title={d.id && d.questionCount > 0 ? 'Move or delete its questions first' : 'Remove section'}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{ padding: '12px 18px', borderTop: '1px solid #f0f0f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <button className="phil-btn phil-btn-secondary phil-btn-sm" onClick={addDraft}>+ Add Section</button>
            <span style={{ fontSize: 11, color: '#aaa' }}>Save, then select a new section's tab to add its questions.</span>
          </div>
        </div>
      )}

      {currentSection && (
        <div className="phil-card">
          <div className="phil-card-head">
            <h2>{currentSection.title}</h2>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              {savedMsg && <span style={{ fontSize: 12, color: '#28C840', fontWeight: 600 }}>{savedMsg}</span>}
              {error && <span style={{ fontSize: 12, color: '#E24B4A' }}>{error}</span>}
              <button className="phil-btn phil-btn-primary phil-btn-sm" onClick={saveSection} disabled={saving}>
                {saving ? 'Saving…' : 'Save Section'}
              </button>
            </div>
          </div>

          <datalist id="aq-subgroups">
            {subgroupOptions.map(s => <option key={s} value={s} />)}
          </datalist>

          <table className="phil-table">
            <thead>
              <tr>
                <th style={{ width: 56 }}>#</th>
                <th>Label</th>
                <th style={{ width: 110 }}>Type</th>
                <th style={{ width: 70 }}>Required</th>
                <th>Options</th>
                <th style={{ width: 100 }}></th>
              </tr>
            </thead>
            <tbody>
              {currentSection.questions.map((q, idx) => (
                editingId === q.id && editState ? (
                  <tr key={q.id} style={{ background: '#f0f7ff' }}>
                    <td style={{ color: '#aaa', fontSize: 12 }}>{idx + 1}</td>
                    <td>
                      <input
                        className="phil-input"
                        value={editState.label}
                        onChange={e => setEditState({ ...editState, label: e.target.value })}
                      />
                      <input
                        className="phil-input"
                        style={{ marginTop: 4, fontSize: 11 }}
                        placeholder="Note (optional)"
                        value={editState.note}
                        onChange={e => setEditState({ ...editState, note: e.target.value })}
                      />
                      <input
                        className="phil-input"
                        style={{ marginTop: 4, fontSize: 11 }}
                        placeholder="Scoring label (optional short wording for evaluators)"
                        value={editState.scoringLabel}
                        onChange={e => setEditState({ ...editState, scoringLabel: e.target.value })}
                      />
                      <input
                        className="phil-input"
                        style={{ marginTop: 4, fontSize: 11 }}
                        list="aq-subgroups"
                        placeholder="Scoring sub-heading (optional, e.g. Founders)"
                        title="Groups this question under a sub-heading in the evaluator scoring view. Leave blank for none."
                        value={editState.subgroup}
                        onChange={e => setEditState({ ...editState, subgroup: e.target.value })}
                      />
                      {fieldKeyOptions.length > 0 && (
                        <select
                          className="phil-select"
                          style={{ marginTop: 4, fontSize: 11 }}
                          title="Tag this question as the source for a data-model field (e.g. the Company name / Industry shown in the pipeline). One question per field, per year."
                          value={editState.fieldKey}
                          onChange={e => setEditState({ ...editState, fieldKey: e.target.value })}
                        >
                          <option value="">Data field: none</option>
                          {fieldKeyOptions.map(o => (
                            <option key={o.key} value={o.key}>Data field: {o.label}</option>
                          ))}
                        </select>
                      )}
                      {editState.type === 'text' && (
                        <FormatPicker
                          pattern={editState.pattern}
                          patternHint={editState.patternHint}
                          onChange={(pattern, patternHint) => setEditState({ ...editState, pattern, patternHint })}
                        />
                      )}
                    </td>
                    <td>
                      <select
                        className="phil-select"
                        value={editState.type}
                        onChange={e => setEditState({ ...editState, type: e.target.value })}
                      >
                        {QUESTION_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                      </select>
                    </td>
                    <td>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, cursor: 'pointer' }}>
                        <input type="checkbox" checked={editState.required} onChange={e => setEditState({ ...editState, required: e.target.checked })} />
                        Yes
                      </label>
                    </td>
                    <td>
                      {needsOptions(editState.type) && (
                        <input
                          className="phil-input"
                          placeholder="Comma-separated options"
                          value={editState.options}
                          onChange={e => setEditState({ ...editState, options: e.target.value })}
                        />
                      )}
                      {editState.type === 'select' && (
                        <label style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, marginTop: 4, cursor: 'pointer', color: '#555' }}
                          title="Applicants type to filter the options instead of scrolling a plain dropdown. Best for long option lists.">
                          <input
                            type="checkbox"
                            checked={editState.searchable}
                            onChange={e => setEditState({ ...editState, searchable: e.target.checked })}
                          />
                          Searchable
                        </label>
                      )}
                      {editState.type === 'number' && (
                        <label style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, marginTop: 4, cursor: 'pointer', color: '#555' }}
                          title="Display the answer with thousands separators (e.g. 1,500,000) for applicants and evaluators. Decimals are preserved.">
                          <input
                            type="checkbox"
                            checked={editState.groupThousands}
                            onChange={e => setEditState({ ...editState, groupThousands: e.target.checked })}
                          />
                          Group with commas
                        </label>
                      )}
                      <select
                        className="phil-select"
                        style={{ marginTop: needsOptions(editState.type) ? 4 : 0, fontSize: 11 }}
                        title="How this answer renders in the evaluator scoring view"
                        value={editState.displayHint}
                        onChange={e => setEditState({ ...editState, displayHint: e.target.value })}
                      >
                        {DISPLAY_HINTS.map(h => <option key={h.value} value={h.value}>Scoring: {h.label}</option>)}
                      </select>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, marginTop: 4, cursor: 'pointer', color: '#555' }}
                        title="Untick to hide this question from the evaluator scoring view">
                        <input
                          type="checkbox"
                          checked={!editState.scoringHidden}
                          onChange={e => setEditState({ ...editState, scoringHidden: !e.target.checked })}
                        />
                        Show in scoring
                      </label>
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button className="phil-btn phil-btn-primary phil-btn-sm" onClick={applyEdit}>Apply</button>
                        <button className="phil-btn phil-btn-secondary phil-btn-sm" onClick={cancelEdit}>Cancel</button>
                      </div>
                    </td>
                  </tr>
                ) : (
                  <tr key={q.id}>
                    <td style={{ color: '#aaa', fontSize: 12 }}>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                        <button
                          className="phil-btn phil-btn-secondary phil-btn-sm"
                          style={{ padding: '0 5px', fontSize: 11, lineHeight: '16px', minWidth: 0, visibility: idx === 0 ? 'hidden' : 'visible' }}
                          onClick={() => moveQuestion(q.id, -1)}
                          title="Move up"
                        >▲</button>
                        <span>{idx + 1}</span>
                        <button
                          className="phil-btn phil-btn-secondary phil-btn-sm"
                          style={{ padding: '0 5px', fontSize: 11, lineHeight: '16px', minWidth: 0, visibility: idx === (currentSection?.questions.length ?? 0) - 1 ? 'hidden' : 'visible' }}
                          onClick={() => moveQuestion(q.id, 1)}
                          title="Move down"
                        >▼</button>
                      </div>
                    </td>
                    <td>
                      <div style={{ fontWeight: 500, fontSize: 13 }}>{q.label}</div>
                      {q.note && <div style={{ fontSize: 11, color: '#F5A623', marginTop: 2 }}>{q.note}</div>}
                      {q.pattern && (
                        <div style={{ fontSize: 11, color: '#00AEEF', marginTop: 2 }}>
                          Format: {formatForPattern(q.pattern)?.label ?? 'Custom'}
                        </div>
                      )}
                      {q.displayHint && (
                        <div style={{ fontSize: 11, color: '#7B61FF', marginTop: 2 }}>
                          Scoring: {DISPLAY_HINTS.find(h => h.value === q.displayHint)?.label ?? q.displayHint}
                        </div>
                      )}
                      {q.scoringLabel && (
                        <div style={{ fontSize: 11, color: '#7B61FF', marginTop: 2 }}>
                          Scoring label: “{q.scoringLabel}”
                        </div>
                      )}
                      {q.subgroup && (
                        <div style={{ fontSize: 11, color: '#7B61FF', marginTop: 2 }}>
                          Scoring sub-heading: “{q.subgroup}”
                        </div>
                      )}
                      {q.fieldKey && (
                        <div style={{ fontSize: 11, color: '#0A8754', marginTop: 2 }}>
                          Data field: {fieldKeyOptions.find(o => o.key === q.fieldKey)?.label ?? q.fieldKey}
                        </div>
                      )}
                      {q.scoringHidden && (
                        <div style={{ fontSize: 11, color: '#E24B4A', marginTop: 2 }}>
                          Hidden from scoring view
                        </div>
                      )}
                    </td>
                    <td><span style={{ fontSize: 11, background: '#f0f0f0', padding: '2px 7px', borderRadius: 3 }}>{q.type}</span></td>
                    <td style={{ fontSize: 12, color: q.required ? '#28C840' : '#aaa' }}>{q.required ? 'Yes' : '—'}</td>
                    <td style={{ fontSize: 12, color: '#555' }}>
                      {q.options ? q.options.join(', ') : <span style={{ color: '#ccc' }}>—</span>}
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button className="phil-btn phil-btn-secondary phil-btn-sm" onClick={() => startEdit(q)}>Edit</button>
                        <button
                          className="phil-btn phil-btn-secondary phil-btn-sm"
                          style={{ color: '#E24B4A' }}
                          onClick={() => deleteQuestion(q.id)}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                )
              ))}

              {/* Add new question row */}
              {addingNew && (
                <tr style={{ background: '#f7fbfe' }}>
                  <td style={{ color: '#aaa', fontSize: 12 }}>New</td>
                  <td>
                    <input
                      className="phil-input"
                      placeholder="Question label"
                      value={newQ.label}
                      onChange={e => setNewQ({ ...newQ, label: e.target.value })}
                      autoFocus
                    />
                    <input
                      className="phil-input"
                      style={{ marginTop: 4, fontSize: 11 }}
                      placeholder="Scoring label (optional short wording for evaluators)"
                      value={newQ.scoringLabel}
                      onChange={e => setNewQ({ ...newQ, scoringLabel: e.target.value })}
                    />
                    <input
                      className="phil-input"
                      style={{ marginTop: 4, fontSize: 11 }}
                      list="aq-subgroups"
                      placeholder="Scoring sub-heading (optional, e.g. Founders)"
                      title="Groups this question under a sub-heading in the evaluator scoring view. Leave blank for none."
                      value={newQ.subgroup}
                      onChange={e => setNewQ({ ...newQ, subgroup: e.target.value })}
                    />
                    {fieldKeyOptions.length > 0 && (
                      <select
                        className="phil-select"
                        style={{ marginTop: 4, fontSize: 11 }}
                        title="Tag this question as the source for a data-model field (e.g. the Company name / Industry shown in the pipeline). One question per field, per year."
                        value={newQ.fieldKey}
                        onChange={e => setNewQ({ ...newQ, fieldKey: e.target.value })}
                      >
                        <option value="">Data field: none</option>
                        {fieldKeyOptions.map(o => (
                          <option key={o.key} value={o.key}>Data field: {o.label}</option>
                        ))}
                      </select>
                    )}
                    {newQ.type === 'text' && (
                      <FormatPicker
                        pattern={newQ.pattern}
                        patternHint={newQ.patternHint}
                        onChange={(pattern, patternHint) => setNewQ({ ...newQ, pattern, patternHint })}
                      />
                    )}
                  </td>
                  <td>
                    <select className="phil-select" value={newQ.type} onChange={e => setNewQ({ ...newQ, type: e.target.value })}>
                      {QUESTION_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </td>
                  <td>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, cursor: 'pointer' }}>
                      <input type="checkbox" checked={newQ.required} onChange={e => setNewQ({ ...newQ, required: e.target.checked })} />
                      Yes
                    </label>
                  </td>
                  <td>
                    {needsOptions(newQ.type) && (
                      <input
                        className="phil-input"
                        placeholder="Comma-separated options"
                        value={newQ.options}
                        onChange={e => setNewQ({ ...newQ, options: e.target.value })}
                      />
                    )}
                    {newQ.type === 'select' && (
                      <label style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, marginTop: 4, cursor: 'pointer', color: '#555' }}
                        title="Applicants type to filter the options instead of scrolling a plain dropdown. Best for long option lists.">
                        <input
                          type="checkbox"
                          checked={newQ.searchable}
                          onChange={e => setNewQ({ ...newQ, searchable: e.target.checked })}
                        />
                        Searchable
                      </label>
                    )}
                    {newQ.type === 'number' && (
                      <label style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, marginTop: 4, cursor: 'pointer', color: '#555' }}
                        title="Display the answer with thousands separators (e.g. 1,500,000) for applicants and evaluators. Decimals are preserved.">
                        <input
                          type="checkbox"
                          checked={newQ.groupThousands}
                          onChange={e => setNewQ({ ...newQ, groupThousands: e.target.checked })}
                        />
                        Group with commas
                      </label>
                    )}
                    <select
                      className="phil-select"
                      style={{ marginTop: needsOptions(newQ.type) ? 4 : 0, fontSize: 11 }}
                      title="How this answer renders in the evaluator scoring view"
                      value={newQ.displayHint}
                      onChange={e => setNewQ({ ...newQ, displayHint: e.target.value })}
                    >
                      {DISPLAY_HINTS.map(h => <option key={h.value} value={h.value}>Scoring: {h.label}</option>)}
                    </select>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, marginTop: 4, cursor: 'pointer', color: '#555' }}
                      title="Untick to hide this question from the evaluator scoring view">
                      <input
                        type="checkbox"
                        checked={!newQ.scoringHidden}
                        onChange={e => setNewQ({ ...newQ, scoringHidden: !e.target.checked })}
                      />
                      Show in scoring
                    </label>
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button className="phil-btn phil-btn-primary phil-btn-sm" onClick={commitNewQuestion}>Add</button>
                      <button className="phil-btn phil-btn-secondary phil-btn-sm" onClick={() => setAddingNew(false)}>Cancel</button>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          {!addingNew && (
            <div style={{ padding: '12px 18px', borderTop: '1px solid #f0f0f0' }}>
              <button className="phil-btn phil-btn-secondary phil-btn-sm" onClick={() => { setAddingNew(true); cancelEdit(); }}>
                + Add Question
              </button>
            </div>
          )}
        </div>
      )}
    </>
  );
}

function needsOptions(type: string) {
  return type === 'select' || type === 'checkboxes';
}

/** Format enforcement picker for text questions: choose a preset, "Custom" (raw
 *  regex + example), or "None". Reports the resolved pattern + hint upward. */
function FormatPicker({ pattern, patternHint, onChange }: {
  pattern: string; patternHint: string; onChange: (pattern: string, patternHint: string) => void;
}) {
  const preset = QUESTION_FORMATS.find(f => f.pattern === pattern);
  const [custom, setCustom] = useState(!!pattern && !preset);
  const value = !pattern && !custom ? 'none' : preset ? preset.key : 'custom';

  const onSelect = (v: string) => {
    if (v === 'none') { setCustom(false); onChange('', ''); return; }
    if (v === 'custom') { setCustom(true); onChange(pattern, patternHint); return; }
    setCustom(false);
    const f = QUESTION_FORMATS.find(x => x.key === v);
    if (f) onChange(f.pattern, f.hint);
  };

  return (
    <div style={{ marginTop: 4 }}>
      <select
        className="phil-select"
        style={{ fontSize: 11 }}
        value={value}
        onChange={e => onSelect(e.target.value)}
      >
        <option value="none">Format: None</option>
        {QUESTION_FORMATS.map(f => <option key={f.key} value={f.key}>Format: {f.label}</option>)}
        <option value="custom">Format: Custom…</option>
      </select>
      {value === 'custom' && (
        <>
          <input
            className="phil-input"
            style={{ marginTop: 4, fontSize: 11 }}
            placeholder="Custom regex (no anchors), e.g. \d{4}"
            value={pattern}
            onChange={e => onChange(e.target.value, patternHint)}
          />
          <input
            className="phil-input"
            style={{ marginTop: 4, fontSize: 11 }}
            placeholder="Example / error hint, e.g. e.g. 2019"
            value={patternHint}
            onChange={e => onChange(pattern, e.target.value)}
          />
        </>
      )}
    </div>
  );
}
